import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { writeAuditLog } from '../utils/auditLog';
import { Lock } from '@phosphor-icons/react';

export default function SettingsPage() {
  const { pharmacy, role, user } = useAuth();
  const [form, setForm] = useState({
    name:         pharmacy?.name         || '',
    city:         pharmacy?.city         || '',
    province:     pharmacy?.province     || '',
    contactEmail: pharmacy?.contactEmail || '',
    timezone:     pharmacy?.timezone     || 'America/Toronto',
      requireReadyInitials: pharmacy?.requireReadyInitials || false,
      requirePickupInitials: pharmacy?.requirePickupInitials || false,
  });
  const [saved, setSaved]   = useState(false);
  const [saving, setSaving] = useState(false);

  if (role !== 'admin' && role !== 'superadmin') {
    return (
      <div style={{ padding: '28px 32px', fontFamily: 'Inter, system-ui, sans-serif' }}>
        <h1 style={{ color: '#0F172A' }}>Settings</h1>
        <p style={{ color: '#94A3B8' }}>Admin access required.</p>
      </div>
    );
  }

  const save = async () => {
    if (!pharmacy) return;
    setSaving(true);
    await updateDoc(doc(db, 'pharmacies', pharmacy.id), { ...form });
    await writeAuditLog(pharmacy.id, user, {
      action: 'Pharmacy settings changed',
      entityType: 'pharmacy',
      entityId: pharmacy.id,
      entityName: pharmacy.name || pharmacy.id,
      previousValue: JSON.stringify({
        name: pharmacy.name || '', city: pharmacy.city || '',
        province: pharmacy.province || '', contactEmail: pharmacy.contactEmail || '',
        timezone: pharmacy.timezone || '',
      }),
      newValue: JSON.stringify(form),
      actorRole: role || '',
      source: 'client',
    });
    setSaved(true);
    setSaving(false);
    setTimeout(() => setSaved(false), 2500);
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB',
    borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#F9FAFB',
  };

  const labelStyle = {
    display: 'block', fontSize: 12, fontWeight: 600,
    color: '#374151', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em',
  };

  const sectionHeader = (title, subtitle) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>{subtitle}</div>}
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', fontFamily: 'Inter, system-ui, sans-serif', maxWidth: 580 }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 800, color: '#0F172A' }}>Settings</h1>
      <p style={{ margin: '0 0 28px', fontSize: 13, color: '#94A3B8' }}>Pharmacy profile and configuration</p>

      {/* Pharmacy Profile Card */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: 28, marginBottom: 20 }}>
        {sectionHeader('Pharmacy Profile', 'Basic information shown to your team')}

        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Pharmacy Name</label>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            style={inputStyle} />
        </div>

        <div style={{ display: 'flex', gap: 14, marginBottom: 18 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>City</label>
            <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
              style={inputStyle} />
          </div>
          <div style={{ width: 100 }}>
            <label style={labelStyle}>Province</label>
            <input value={form.province} onChange={e => setForm({ ...form, province: e.target.value })}
              style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Contact Email</label>
          <input type="email" value={form.contactEmail}
            onChange={e => setForm({ ...form, contactEmail: e.target.value })}
            style={inputStyle} />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Timezone</label>
          <select value={form.timezone} onChange={e => setForm({ ...form, timezone: e.target.value })}
            style={inputStyle}>
            <option value="America/Toronto">America/Toronto (Eastern)</option>
            <option value="America/Winnipeg">America/Winnipeg (Central)</option>
            <option value="America/Edmonton">America/Edmonton (Mountain)</option>
            <option value="America/Vancouver">America/Vancouver (Pacific)</option>

          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Staff Initials</label>
            <label style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <input type="checkbox" checked={!!form.requireReadyInitials} onChange={e => setForm({ ...form, requireReadyInitials: e.target.checked })} />
              Require initials when marking Ready
            </label>
            <label style={{ display:"flex", alignItems:"center", gap:8 }}>
              <input type="checkbox" checked={!!form.requirePickupInitials} onChange={e => setForm({ ...form, requirePickupInitials: e.target.checked })} />
              Require initials when recording Pickup
            </label>
          </div>
          </select>
        </div>

        <button onClick={save} disabled={saving} style={{
          padding: '10px 28px',
          background: saved ? '#16A34A' : saving ? '#94A3B8' : '#0F172A',
          color: '#fff', border: 'none', borderRadius: 8, fontSize: 14,
          fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', transition: 'background 0.2s',
        }}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
        </button>
      </div>

      {/* Accreditation Card — read only */}
      <div style={{ background: '#F8FAFC', borderRadius: 12, border: '1px solid #E2E8F0', padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Lock size={15} weight="bold" color="#94A3B8" />
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>Accreditation</div>
        </div>
        <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 20 }}>
          Read-only. Contact support to update accreditation details.
        </div>

        <div style={{ display: 'flex', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={{ ...labelStyle, color: '#94A3B8' }}>OCP Accreditation #</label>
            <div style={{
              padding: '10px 12px', background: '#F1F5F9', borderRadius: 8,
              border: '1px solid #E2E8F0', fontSize: 14, color: '#475569', fontWeight: 500,
            }}>
              {pharmacy?.ocpAccreditation || '—'}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ ...labelStyle, color: '#94A3B8' }}>Pharmacy ID</label>
            <div style={{
              padding: '10px 12px', background: '#F1F5F9', borderRadius: 8,
              border: '1px solid #E2E8F0', fontSize: 13, color: '#94A3B8', fontFamily: 'monospace',
            }}>
              {pharmacy?.id || '—'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
